import { saveAs } from "file-saver";
import Papa from "papaparse";
import React, { useState } from "react";
import { Button, Form, Icon, Modal, Table } from "semantic-ui-react";
import styled from "styled-components";
import {
  Device,
  fetchAllDevices,
  fetchDevicesCount,
  searchDevices,
  uploadFile as uploadMultipartFile,
} from "../../../../../BytebeamClient";
import { useUser } from "../../../../../context/User.context";
import { beamtoast } from "../../../../common/CustomToast";
import { ErrorMessage } from "../../../../common/ErrorMessage";
import LoadingAnimation from "../../../../common/Loader";
import { ThinDivider } from "../../../Dashboards/Panel/util";
import AnimatedEllipsis from "../../../common/AnimatedEllipsis";
import { getFileExtension } from "../../../util";
import { downloadDevicesData } from "./DownloadMetadataModal";
import { StyledFileInput, StyledFileUploadButton } from "./SendFileModal";

export const TemplateDownloaderButton = styled.span`
  font-weight: 700;
  color: #1158e1;
  cursor: pointer;
`;

export const MessageContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  min-height: 64px;
  p {
    font-size: 15px;
  }
`;

const SCIENTIFIC_NOTATION_REGEX = /^-?\d+(\.\d+)?[eE][+-]?\d+$/;
const MAX_ROWS = 10000;

type CsvRowData = Record<string, string | undefined>;

type MetadataDiffChange = {
  key: string;
  oldValue: string;
  newValue: string;
  changeType: "modified" | "added" | "removed";
};

type MetadataDiff = {
  deviceId: number | string;
  changes: MetadataDiffChange[];
};

async function parseCsvFile(file: File): Promise<CsvRowData[]> {
  let text = await file.text();
  if (text.charCodeAt(0) === 0xfeff) {
    text = text.slice(1);
  }
  const parsed = Papa.parse<CsvRowData>(text, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (h: string) => h.replace(/^\uFEFF/, "").trim(),
  });
  if (parsed.errors.length > 0) {
    const fatal = parsed.errors.find(
      (e) => e.type === "Quotes" || e.row != null
    );
    if (fatal) {
      throw new Error(parsed.errors.map((e) => e.message).join("; "));
    }
  }
  return (parsed.data || []).filter(
    (row) =>
      row &&
      Object.keys(row).some(
        (k) => row[k] != null && String(row[k]).trim() !== ""
      )
  );
}

function getDeviceIdFromRow(row: CsvRowData): string | undefined {
  const raw =
    row.id ?? row["Device Id"] ?? row["device id"] ?? row["Device ID"];
  if (raw === undefined || raw === null) {
    return undefined;
  }
  const s = String(raw).trim();
  return s === "" ? undefined : s;
}

function normalizeCsvRowsForDiff(rows: CsvRowData[]): CsvRowData[] {
  return rows.map((row) => {
    const id = getDeviceIdFromRow(row);
    const next = { ...row };
    if (id !== undefined) {
      next.id = id;
    }
    delete next["Device Id"];
    delete next["device id"];
    delete next["Device ID"];
    return next;
  });
}

function generateMetadataDiff(
  csvData: CsvRowData[],
  devices: Device[]
): { diffs: MetadataDiff[]; invalidIds: string[] } {
  const deviceById = new Map<string, Device>(
    devices.map((d) => [String(d.id), d])
  );
  const diffs: MetadataDiff[] = [];
  const invalidIds: string[] = [];

  for (const row of csvData) {
    const rowId = getDeviceIdFromRow(row);
    if (!rowId) {
      continue;
    }
    const device = deviceById.get(String(rowId));
    if (!device) {
      invalidIds.push(String(rowId));
      continue;
    }

    const changes: MetadataDiffChange[] = [];
    for (const [key, newVal] of Object.entries(row)) {
      if (
        key === "id" ||
        key === "Device Id" ||
        key === "device id" ||
        key === "Device ID"
      ) {
        continue;
      }

      const currentValue = device.metadata?.[key];
      const currentString =
        currentValue === null || currentValue === undefined
          ? ""
          : String(currentValue);
      const newString = String(newVal ?? "");

      if (currentString !== newString) {
        let changeType: MetadataDiffChange["changeType"] = "modified";
        if (!currentString && newString) {
          changeType = "added";
        } else if (currentString && !newString) {
          changeType = "removed";
        }
        changes.push({
          key,
          oldValue: currentString,
          newValue: newString,
          changeType,
        });
      }
    }

    if (changes.length > 0) {
      diffs.push({ deviceId: device.id, changes });
    }
  }

  return { diffs, invalidIds };
}

async function fetchDevicesForRows(ids: string[]): Promise<Device[]> {
  const unique = [...new Set(ids.map(String))];
  if (unique.length === 0) {
    return [];
  }
  const res = await searchDevices(
    { metaDatafilters: { id: unique } },
    1,
    Math.min(unique.length, MAX_ROWS),
    "active"
  );
  return res.devices;
}

async function buildNormalizedUploadCsv(uploadFile: File): Promise<File> {
  const csvText = await uploadFile.text();
  const parsed = Papa.parse<CsvRowData>(csvText, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (h: string) => h.replace(/^\uFEFF/, "").trim(),
  });
  const normalized = (parsed.data || []).map((row) => {
    const id = getDeviceIdFromRow(row);
    const rest = { ...row };
    delete rest["Device Id"];
    delete rest["device id"];
    delete rest["Device ID"];
    if (id !== undefined) {
      rest.id = id;
    }
    return rest;
  });
  const renamedCsvText = Papa.unparse(normalized);
  return new File([renamedCsvText], uploadFile.name, {
    type: uploadFile.type || "text/csv",
  });
}

type RenderUpdatingMetadataFailedProps = {
  readonly close: (...args: any[]) => any;
};

function RenderUpdatingMetadataFailed(
  props: RenderUpdatingMetadataFailedProps
) {
  const { close } = props;
  return (
    <>
      <Modal.Content>
        <Modal.Description>
          <MessageContainer>
            <ErrorMessage
              errorMessage
              marginTop="15px"
              marginBottom="10px"
              iconSize="big"
              messageLess
            />
            <p style={{ marginTop: "12px" }}>
              Updating device metadata failed. Please try again. If the issue
              persists, please contact{" "}
              <a href="mailto:support@bytebeam.io"> support@bytebeam.io </a>{" "}
              from your registered email id.
            </p>
          </MessageContainer>
        </Modal.Description>
      </Modal.Content>
      <Modal.Actions>
        <Button secondary onClick={() => close()}>
          <Icon name="angle left" /> Back
        </Button>
        <Button primary disabled={true}>
          <Icon name="checkmark" /> Update Metadata
        </Button>
      </Modal.Actions>
    </>
  );
}

type RenderMetadataSuccessfullyUpdatedProps = {
  readonly close: (...args: any[]) => any;
};

function RenderMetadataSuccessfullyUpdated(
  props: RenderMetadataSuccessfullyUpdatedProps
) {
  const { close } = props;

  return (
    <>
      <Modal.Content>
        <Modal.Description>
          <MessageContainer>
            <Icon name="check circle outline" size="huge" />
            <p style={{ marginTop: "12px" }}>
              Congratulations! Your device metadata has been successfully
              updated for the required devices.
            </p>
          </MessageContainer>
        </Modal.Description>
      </Modal.Content>
      <Modal.Actions>
        <Button secondary onClick={() => close()}>
          <Icon name="angle left" /> Back
        </Button>
        <Button primary disabled={true}>
          <Icon name="checkmark" /> Update Metadata
        </Button>
      </Modal.Actions>
    </>
  );
}

enum UpdateMetadataStatus {
  TriggerMetadataUpdate,
  UpdatingMetadataFailed,
  MetadataSuccessfullyUpdated,
}

type RenderReviewMetadataChangesProps = {
  readonly metadataDiff: MetadataDiff[];
  readonly fileWarning?: string;
  readonly serialLabel: string;
  readonly onBack: () => void;
  readonly onConfirm: () => void;
  readonly buttonDisabled: boolean;
  readonly showUploadProgress: boolean;
  readonly fileTotal: number;
  readonly fileLoaded: number;
};

function RenderReviewMetadataChanges(props: RenderReviewMetadataChangesProps) {
  const {
    metadataDiff,
    fileWarning,
    serialLabel,
    onBack,
    onConfirm,
    buttonDisabled,
    showUploadProgress,
    fileTotal,
    fileLoaded,
  } = props;

  const allChangeKeys = Array.from(
    new Set(metadataDiff.flatMap((d) => d.changes.map((c) => c.key)))
  );

  const colorForType = (t: MetadataDiffChange["changeType"]) => {
    if (t === "added") {
      return "#21ba45";
    }
    if (t === "removed") {
      return "#db2828";
    }
    return "#fbbd08";
  };

  return (
    <>
      <Modal.Content style={{ maxHeight: "70vh", overflow: "auto" }}>
        <Modal.Description>
          <div
            style={{
              fontSize: "16px",
              fontWeight: 700,
              marginBottom: "12px",
            }}
          >
            Review metadata changes
          </div>
          {fileWarning && (
            <p style={{ color: "#fbbd08", marginBottom: "12px" }}>
              {fileWarning}
            </p>
          )}
          {metadataDiff.length === 0 && (
            <p style={{ marginBottom: "12px", fontSize: "14px" }}>
              No field-level changes to preview; confirming will still upload
              your CSV for the server to process.
            </p>
          )}
          <div
            style={{
              display: "flex",
              gap: "16px",
              marginBottom: "12px",
              fontSize: "13px",
            }}
          >
            <span>
              <span
                style={{
                  display: "inline-block",
                  width: 8,
                  height: 8,
                  borderRadius: 4,
                  background: "#21ba45",
                  marginRight: 6,
                }}
              />
              Added
            </span>
            <span>
              <span
                style={{
                  display: "inline-block",
                  width: 8,
                  height: 8,
                  borderRadius: 4,
                  background: "#db2828",
                  marginRight: 6,
                }}
              />
              Removed
            </span>
            <span>
              <span
                style={{
                  display: "inline-block",
                  width: 8,
                  height: 8,
                  borderRadius: 4,
                  background: "#fbbd08",
                  marginRight: 6,
                }}
              />
              Modified
            </span>
          </div>

          {metadataDiff.length > 0 && (
            <Table celled compact size="small">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>{serialLabel}</Table.HeaderCell>
                  {allChangeKeys.map((fieldKey) => (
                    <Table.HeaderCell key={fieldKey}>
                      {fieldKey}
                    </Table.HeaderCell>
                  ))}
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {metadataDiff.map((deviceDiff) => (
                  <Table.Row key={String(deviceDiff.deviceId)}>
                    <Table.Cell>{deviceDiff.deviceId}</Table.Cell>
                    {allChangeKeys.map((fieldKey) => {
                      const change = deviceDiff.changes.find(
                        (c) => c.key === fieldKey
                      );
                      return (
                        <Table.Cell key={fieldKey}>
                          {change ? (
                            <span style={{ fontSize: "13px" }}>
                              <span
                                style={{
                                  color: colorForType(change.changeType),
                                  fontWeight: 600,
                                }}
                              >
                                {change.oldValue || "empty"}
                              </span>
                              {" → "}
                              <span
                                style={{
                                  color: colorForType(change.changeType),
                                  fontWeight: 600,
                                }}
                              >
                                {change.newValue || "empty"}
                              </span>
                            </span>
                          ) : (
                            <span style={{ color: "#888" }}>—</span>
                          )}
                        </Table.Cell>
                      );
                    })}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}

          {showUploadProgress && (
            <Form.Field style={{ marginTop: "16px" }}>
              <label htmlFor="file-progress-review">
                Updating Metadata in Bulk
                <AnimatedEllipsis spacing={3} dotSize={"8px"} />
              </label>
              <progress
                id="file-progress-review"
                max={fileTotal}
                value={fileLoaded}
              />
            </Form.Field>
          )}
        </Modal.Description>
      </Modal.Content>
      <Modal.Actions>
        <Button secondary onClick={onBack} disabled={buttonDisabled}>
          <Icon name="angle left" /> Back
        </Button>
        <Button primary disabled={buttonDisabled} onClick={onConfirm}>
          <Icon name="checkmark" /> Confirm &amp; Update Metadata
        </Button>
      </Modal.Actions>
    </>
  );
}

type RenderTriggerMetadataUpdateProps = {
  readonly close: (...args: any[]) => any;
  readonly setStatus: (...args: any[]) => any;
  readonly setLoading: (...args: any[]) => void;
  readonly refreshDevices: (inLoop?: boolean) => Promise<void>;
  readonly isRefreshPaused: boolean;
};

function RenderTriggerMetadataUpdate(props: RenderTriggerMetadataUpdateProps) {
  const { close, setStatus, setLoading, refreshDevices, isRefreshPaused } =
    props;

  const { user } = useUser();
  const serial_metadataKey = user?.["tenant-settings"]?.["serial-key"];
  const serialLabel = serial_metadataKey
    ? `#${serial_metadataKey}`
    : "Device id";
  const permissions = user?.role?.permissions;

  const [file, setFile] = useState<File>(new File([""], ""));
  const [fileName, setFileName] = useState<string>("");
  const [showUploadProgress, setShowUploadProgress] = useState<boolean>(false);
  const [fileTotal, setFileTotal] = useState<number>(0);
  const [fileLoaded, setFileLoaded] = useState<number>(0);
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);
  const [downloadingTemplate, setDownloadingTemplate] =
    useState<boolean>(false);
  const [fileError, setFileError] = useState<string>("");
  const [fileWarning, setFileWarning] = useState<string>("");
  const [metadataDiff, setMetadataDiff] = useState<MetadataDiff[]>([]);
  const [parseReady, setParseReady] = useState<boolean>(false);
  const [parsingFile, setParsingFile] = useState<boolean>(false);
  const [showReview, setShowReview] = useState<boolean>(false);

  async function downloadTemplate() {
    setDownloadingTemplate(true);

    try {
      const totalDevices = await fetchDevicesCount("active");
      const devices = await fetchAllDevices(1, totalDevices);
      let csvData: BlobPart = await downloadDevicesData(
        devices,
        permissions,
        true,
        false,
        false,
        true
      );
      csvData = String(csvData).replace("Device Id", "id");
      const templateFile = new File([csvData], "update metadata template.csv", {
        type: "text/csv",
      });
      saveAs(templateFile);
    } catch (error) {
      console.log("Error While Downloading Metadata Template: ", error);
    } finally {
      setDownloadingTemplate(false);
    }
  }

  async function onSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const csvFile = e.target.files?.[0];
    const resetInput = () => {
      e.target.value = "";
    };

    setFileError("");
    setFileWarning("");
    setParseReady(false);
    setMetadataDiff([]);
    setShowReview(false);

    if (!csvFile) {
      setFile(new File([""], ""));
      setFileName("");
      return;
    }

    setFile(csvFile);
    setFileName(csvFile.name);
    setParsingFile(true);

    try {
      const rawRows = await parseCsvFile(csvFile);
      if (rawRows.length === 0) {
        setFileError("The CSV file is empty or has no data rows.");
        setFile(new File([""], ""));
        setFileName("");
        resetInput();
        return;
      }

      const first = rawRows[0];
      const hasId =
        first &&
        ("id" in first ||
          "Device Id" in first ||
          "device id" in first ||
          "Device ID" in first);
      if (!hasId) {
        setFileError(
          `Missing device id column. Include an "id" column (or "Device Id" from older templates).`
        );
        setFile(new File([""], ""));
        setFileName("");
        resetInput();
        return;
      }

      if (rawRows.length > MAX_ROWS) {
        setFileError(
          `File contains ${rawRows.length} rows. Maximum allowed is ${MAX_ROWS.toLocaleString()}.`
        );
        setFile(new File([""], ""));
        setFileName("");
        resetInput();
        return;
      }

      const normalized = normalizeCsvRowsForDiff(rawRows);
      const ids = normalized
        .map((row) => getDeviceIdFromRow(row))
        .filter((id): id is string => Boolean(id));

      const emptyId = normalized.some((row) => !getDeviceIdFromRow(row));
      if (emptyId) {
        setFileError(
          "Some rows are missing a device id. Every row must include a valid id."
        );
        setFile(new File([""], ""));
        setFileName("");
        resetInput();
        return;
      }

      const seen = new Set<string>();
      const dup = new Set<string>();
      for (const id of ids) {
        if (seen.has(id)) {
          dup.add(id);
        }
        seen.add(id);
      }
      if (dup.size > 0) {
        const list = [...dup].slice(0, 5).join(", ");
        const more = dup.size > 5 ? ` and ${dup.size - 5} more` : "";
        setFileError(
          `Duplicate device ids found: ${list}${more}. Each device must appear only once.`
        );
        setFile(new File([""], ""));
        setFileName("");
        resetInput();
        return;
      }

      const sciCols = new Set<string>();
      for (const row of rawRows) {
        for (const [key, value] of Object.entries(row)) {
          const v = value;
          if (
            (typeof v === "string" || typeof v === "number") &&
            SCIENTIFIC_NOTATION_REGEX.test(String(v).trim())
          ) {
            sciCols.add(key);
          }
        }
        if (sciCols.size > 0) {
          break;
        }
      }
      if (sciCols.size > 0) {
        setFileError(
          `Scientific notation found in column(s): ${[...sciCols].join(
            ", "
          )}. Reformat values as plain numbers or text before uploading.`
        );
        setFile(new File([""], ""));
        setFileName("");
        resetInput();
        return;
      }

      const devices = await fetchDevicesForRows(ids);
      const { diffs, invalidIds } = generateMetadataDiff(normalized, devices);

      if (invalidIds.length > 0) {
        const display = invalidIds.slice(0, 5).join(", ");
        const more =
          invalidIds.length > 5 ? ` and ${invalidIds.length - 5} more` : "";
        setFileError(
          `Unknown or inactive device id(s): ${display}${more}. Ids must match active devices you can access.`
        );
        setFile(new File([""], ""));
        setFileName("");
        resetInput();
        return;
      }

      setMetadataDiff(diffs);
      if (diffs.length === 0) {
        setFileWarning(
          "No changes detected compared to current metadata. You can still proceed to upload."
        );
      }
      setParseReady(true);
    } catch (err) {
      console.error("Failed to parse CSV:", err);
      setFileError(
        "Failed to parse CSV. Check quoting, commas, and file encoding."
      );
      setFile(new File([""], ""));
      setFileName("");
      resetInput();
    } finally {
      setParsingFile(false);
    }
  }

  async function runUpload(uploadFile: File) {
    setButtonDisabled(true);
    const formData = new FormData();
    const normalizedFile = await buildNormalizedUploadCsv(uploadFile);
    formData.append("file", normalizedFile);

    const url = `/api/v1/devices/metadata-bulk`;
    try {
      const res = await uploadMultipartFile(url, formData, (p) => {
        setFileTotal(p.total);
        setFileLoaded(p.loaded);
      });

      if (res.status === 200) {
        setStatus(UpdateMetadataStatus.MetadataSuccessfullyUpdated);
        if (isRefreshPaused) {
          refreshDevices(true);
        } else {
          setLoading();
        }
      } else {
        setStatus(UpdateMetadataStatus.UpdatingMetadataFailed);
      }
    } catch (error: any) {
      if (error?.response?.data?.error) {
        const errorMessage: string = error.response.data.error.message;

        if (errorMessage.toLowerCase().includes("must be unique")) {
          beamtoast.error(
            `'${serial_metadataKey}' metadata values must be unique`
          );
        } else if (
          errorMessage
            .toLowerCase()
            .includes("serial key value cannot be empty")
        ) {
          beamtoast.error(
            `'${serial_metadataKey}' metadata values cannot be null or empty`
          );
        } else {
          beamtoast.error(errorMessage);
        }
      }
      setStatus(UpdateMetadataStatus.UpdatingMetadataFailed);
      console.log("Error While Updating Metadata - ", error.response);
    } finally {
      setButtonDisabled(false);
      setShowUploadProgress(false);
    }
  }

  function goToReview() {
    if (!parseReady || file?.size === 0) {
      setFileError("Please select a valid CSV file first.");
      return;
    }
    setFileError("");
    setShowReview(true);
  }

  async function confirmUploadFromReview() {
    const extension = getFileExtension(file.name);
    if (extension !== "csv") {
      beamtoast.error("Invalid file type");
      return;
    }
    setShowUploadProgress(true);
    await runUpload(file);
  }

  if (showReview) {
    return (
      <RenderReviewMetadataChanges
        metadataDiff={metadataDiff}
        fileWarning={fileWarning}
        serialLabel={serialLabel}
        onBack={() => setShowReview(false)}
        onConfirm={confirmUploadFromReview}
        buttonDisabled={buttonDisabled}
        showUploadProgress={showUploadProgress}
        fileTotal={fileTotal}
        fileLoaded={fileLoaded}
      />
    );
  }

  return (
    <>
      <Modal.Content>
        <Modal.Description>
          {downloadingTemplate ? (
            <div
              style={{
                height: "64px",
              }}
            >
              <LoadingAnimation
                loadingText="Downloading Metadata Bulk Update Template..."
                loaderBorderSize="3px"
                loaderSize="30px"
                fontSize="12px"
              />
            </div>
          ) : (
            <div>
              <div
                style={{
                  fontSize: "16px",
                  fontWeight: 700,
                  paddingBottom: "20px",
                  height: "64px",
                }}
              >
                Download template file to fill all devices metadata which needs
                to be updated.
              </div>
              <TemplateDownloaderButton
                id="downloadTemplateButton"
                onClick={async () => {
                  await downloadTemplate();
                }}
              >
                Click here
              </TemplateDownloaderButton>{" "}
              to download the template file.
            </div>
          )}

          <ThinDivider
            style={{
              marginTop: "15px",
            }}
          />

          <Form>
            <Form.Field>
              <label
                style={{
                  fontSize: "16px",
                  fontWeight: 700,
                }}
              >
                Upload File from the system:{" "}
              </label>
              <div style={{ position: "relative", marginTop: "12px" }}>
                <StyledFileUploadButton
                  fluid
                  content="Select File"
                  labelPosition="left"
                  icon="file"
                  loading={parsingFile}
                  disabled={parsingFile}
                />
                <StyledFileInput
                  type="file"
                  id="file"
                  accept=".csv"
                  onChange={onSelect}
                  disabled={parsingFile}
                />
              </div>
              <label
                htmlFor="warning"
                style={{ marginLeft: "10px", marginTop: "8px", color: "grey" }}
              >
                *File upload size is limited to 1GiB and{" "}
                {MAX_ROWS.toLocaleString()} rows of data. CSV is parsed with
                proper quoting (use this template format).
              </label>
              <label htmlFor="file_name" style={{ marginTop: "12px" }}>
                File Chosen:{" "}
              </label>
              <div id="file_name">
                {fileName !== "" ? fileName : "No File Chosen"}
              </div>
            </Form.Field>

            {fileError && (
              <p style={{ color: "#db2828", marginTop: "8px" }}>{fileError}</p>
            )}
            {fileWarning && !fileError && (
              <p style={{ color: "#fbbd08", marginTop: "8px" }}>
                {fileWarning}
              </p>
            )}

            {showUploadProgress && (
              <Form.Field>
                <label htmlFor="file-progress">
                  Updating Metadata in Bulk
                  <AnimatedEllipsis spacing={3} dotSize={"8px"} />
                </label>
                <progress
                  id="file-progress"
                  max={fileTotal}
                  value={fileLoaded}
                />
              </Form.Field>
            )}
          </Form>
        </Modal.Description>
      </Modal.Content>
      <Modal.Actions>
        <Button
          secondary
          onClick={() => {
            close();
          }}
        >
          <Icon name="angle left" /> Back
        </Button>
        <Button
          primary
          disabled={
            buttonDisabled ||
            parsingFile ||
            file?.size === 0 ||
            !parseReady ||
            !!fileError
          }
          onClick={goToReview}
        >
          <Icon name="exchange" /> Review changes
        </Button>
      </Modal.Actions>
    </>
  );
}

type BulkMetadataUpdateModalProps = {
  readonly close: (...args: any[]) => any;
  readonly setLoading: (...args: any[]) => void;
  readonly refreshDevices: (inLoop?: boolean) => Promise<void>;
  readonly isRefreshPaused: boolean;
};

function BulkMetadataUpdateModal(props: BulkMetadataUpdateModalProps) {
  const { close, setLoading, refreshDevices, isRefreshPaused } = props;

  const [status, setStatus] = useState<number>(
    UpdateMetadataStatus.TriggerMetadataUpdate
  );

  switch (status) {
    case UpdateMetadataStatus.TriggerMetadataUpdate:
      return (
        <RenderTriggerMetadataUpdate
          close={close}
          setStatus={setStatus}
          setLoading={setLoading as (...args: any[]) => void}
          refreshDevices={refreshDevices}
          isRefreshPaused={isRefreshPaused}
        />
      );

    case UpdateMetadataStatus.UpdatingMetadataFailed:
      return <RenderUpdatingMetadataFailed close={close} />;

    case UpdateMetadataStatus.MetadataSuccessfullyUpdated:
      return <RenderMetadataSuccessfullyUpdated close={close} />;

    default:
      return null;
  }
}

export default BulkMetadataUpdateModal;
